import i18n from "i18next"
import { InitOptions } from "i18next"
import { initReactI18next } from "react-i18next/initReactI18next"
import LanguageDetector from "i18next-browser-languagedetector"
import Backend from "i18next-http-backend"

import { UI_I18N_NAMESPACE, uiResources } from "@f2e/ui/i18n"
import { syncDocumentLanguage } from "./documentLanguage"

const isDevelopment = import.meta.env.DEV

// Bundled resources for @f2e/ui namespace (loaded synchronously)
const bundledResources = {
  en: { [UI_I18N_NAMESPACE]: uiResources.en },
  "zh-TW": { [UI_I18N_NAMESPACE]: uiResources["zh-TW"] },
  ja: { [UI_I18N_NAMESPACE]: uiResources.ja },
}

// Do not define the 'lng' option if using a language detector.
const commonConfig: InitOptions = {
  backend: {
    loadPath: isDevelopment
      ? "/src/i18n/locales/{{lng}}/{{ns}}.json" // Development: separate files
      : "/locales/{{lng}}/translation.json", // Production: combined file in dist
    allowMultiLoading: false,
    crossDomain: false,
  },
  fallbackLng: "en",
  supportedLngs: ["en", "zh-TW", "ja"],
  interpolation: {
    escapeValue: false,
  },
  load: "currentOnly" as const,
  react: {
    useSuspense: false,
  },
}

const developmentConfig: InitOptions = {
  ...commonConfig,
  returnNull: true,
  returnEmptyString: true,
  partialBundledLanguages: true,
  nsSeparator: ".", // To support nested namespaces in production
  debug: isDevelopment,
  saveMissing: true, // Automatically log missing translation keys in development
  saveMissingTo: "fallback", // Log to console instead of POSTing to backend
  ns: [
    "common", // src/components/**/*.tsx
    "constants", // src/constants/**/*.ts
    "home", // src/pages/Home/**/*.tsx
    "dashboard", // src/pages/Dashboard/**/*.tsx
    "workspace", // src/pages/Workspace/**/*.tsx
    "crm", // src/pages/Workspace/components/UserManagementTab/**/*.tsx
    "settings", // src/pages/Settings/**/*.tsx
    "historyList", // src/pages/History/**/*.tsx
    "history", // src/pages/History/**/*.tsx
    "connection", // src/pages/Connection/**/*.tsx
    "knowledgeBase", // src/pages/KnowledgeBase/**/*.tsx
    "types", // src/types/**/*.ts
    "metrics", // src/components/Metrics/**/*.tsx
    "evaluation", // src/components/Evaluation/**/*.tsx, src/pages/Workspace/EvaluationTablePage.tsx
    "brands", // src/pages/Brand/**/*.tsx
    "billing", // src/lib/errors.ts, billing/plan limit errors
    UI_I18N_NAMESPACE, // @f2e/ui components
  ],
  resources: bundledResources,
}

const productionConfig: InitOptions = {
  ...commonConfig,
  returnNull: false,
  returnEmptyString: false,
  partialBundledLanguages: false,
  initImmediate: false,
  backend: {
    ...commonConfig.backend,
    queryStringParams: {
      t: import.meta.env.VITE_RELEASE,
    },
  },
}

i18n
  .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init(isDevelopment ? developmentConfig : productionConfig)
  .then(() => {
    syncDocumentLanguage(i18n.resolvedLanguage || i18n.language)

    // Register UI package translations after initialization
    Object.entries(uiResources).forEach(([lng, resources]) => {
      i18n.addResourceBundle(lng, UI_I18N_NAMESPACE, resources, true, true)
    })
  })

i18n.on("languageChanged", syncDocumentLanguage)

export const i18nInstance = i18n
export default i18nInstance
